import { validateInviteMembersQueryParams } from "@circle-react/components/SettingsApp/ManageMembers/InviteMembers/queryParamsSchema";
import { useMasqueradeContext } from "@circle-react/contexts/masqueradeContext";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useDevelopersAccessFlags } from "@circle-react/hooks/developers/useDevelopersAccessFlags";
import { adminDashboardRoutes } from "@circle-react/lib/routes/adminDashboard";
import { coursesRoutes } from "@circle-react/lib/routes/courses";
import { siteRoutes } from "@circle-react/lib/routes/site";
import { workflowsRoutes } from "@circle-react/lib/routes/workflows";
import { Badge } from "@circleco/compass/components/Badge";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Link, useLocation } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import {
  memberSettingsPaths,
  planSettingsPath,
} from "@/react/helpers/urlHelpers";
import { useBrandedAppsFeatureFlags } from "@/react/hooks/useBrandedAppsFeatureFlags";

import { adminV4Icons } from "../adminV4Icons";

const MENU_MIN_WIDTH_CHARS = 28;

// Compass Menu does not support min-width, so we pad the first item label with
// non-breaking spaces to enforce a minimum menu width.
const padMenuLabel = (label: string) =>
  `${label}${"\u00A0".repeat(Math.max(0, MENU_MIN_WIDTH_CHARS - label.length))}`;

interface UseAdminMenuOptionsProps {
  content: MenuOptions;
  hasAIAgentsAccess?: boolean;
  isAffiliatesEnabled: boolean;
  isCoursesV2Enabled: boolean;
  isWorkflowsEnabled: boolean;
  memberCount?: number;
  showCustomizeTheme: boolean;
}

export const useAdminMenuOptions = ({
  memberCount,
  showCustomizeTheme,
  content,
  isWorkflowsEnabled,
  isAffiliatesEnabled,
  hasAIAgentsAccess = true,
  isCoursesV2Enabled = false,
}: UseAdminMenuOptionsProps): MenuOptions => {
  const {
    adminDashboardIcon,
    membersIcon,
    coursesIcon,
    marketingHubIcon,
    workflowsIcon,
    paywallsIcon,
    plansIcon,
    analyticsIcon,
    affiliatesIcon,
    settingsIcon,
    siteIcon,
    developersIcon,
    customizeThemeIcon,
    inviteMemberIcon,
    plusIcon,
    aiAgentsIcon,
    aiInboxIcon,
    viewAsIcon,
  } = adminV4Icons;
  const { isOverviewTabEnabled } = useDevelopersAccessFlags();
  const { brandedAppsBuilder, brandedApp } = useBrandedAppsFeatureFlags();
  const { isBannerVisible, showBanner } = useMasqueradeContext();
  const pathname = useLocation({
    select: location => location.pathname,
  });

  // TODO: Consider memoizing this as it will return a new array on every render
  const options: MenuOptions = [
    {
      label: padMenuLabel(t("sidebar_admin_dashboard")),
      icon: adminDashboardIcon,
      renderLink: <Link to={adminDashboardRoutes.index()} />,
    },
    {
      label: t("section_audience"),
      icon: membersIcon,
      renderLink: <Link to={memberSettingsPaths.index()} />,
      ...(memberCount && {
        suffix: <Badge label={String(memberCount)} variant="primary" />,
      }),
    },
    ...content,
    ...(isCoursesV2Enabled
      ? [
          {
            label: t("sidebar_courses"),
            icon: coursesIcon,
            renderLink: <Link to={coursesRoutes.overview()} />,
          },
        ]
      : []),
    {
      label: t("sidebar_email"),
      icon: marketingHubIcon,
      renderLink: <Link to={settingsNavPathV3.email.overview} />,
    },
    ...(isWorkflowsEnabled
      ? [
          {
            label: t("sidebar_workflows"),
            icon: workflowsIcon,
            renderLink: <Link to={workflowsRoutes.list()} />,
          },
        ]
      : []),
    {
      label: t("analytics.analytics"),
      icon: analyticsIcon,
      renderLink: <Link to="/settings/analytics" />,
    },
    {
      label: t("sidebar_ai_agents"),
      icon: aiAgentsIcon,
      renderLink: <Link to="/settings/ai-agents/agents" />,
    },
    ...(hasAIAgentsAccess
      ? [
          {
            label: t("sidebar_ai_inbox"),
            icon: aiInboxIcon,
            renderLink: <Link to="/settings/ai-inbox" />,
          },
        ]
      : []),
    {
      label: t("sidebar_paywalls"),
      icon: paywallsIcon,
      renderLink: <Link to="/settings/paywalls" />,
    },
    ...(isAffiliatesEnabled
      ? [
          {
            label: t("sidebar_paywalls_affiliates"),
            icon: affiliatesIcon,
            renderLink: <Link to="/settings/affiliates_settings" />,
          },
        ]
      : []),
    {
      label: t("settings.billing.plans.title"),
      icon: plansIcon,
      renderLink: <Link to={planSettingsPath()} />,
    },
    ...(brandedApp.isEnabled
      ? [
          {
            label: t("settings_nav_v3.settings.branded_app"),
            icon: plusIcon,
            renderLink: (
              <Link
                to={
                  brandedAppsBuilder.isEnabled
                    ? "/settings/plus"
                    : "/settings/plus/lite"
                }
              />
            ),
          },
        ]
      : []),
    {
      label: t("settings_nav_v3.site.label"),
      icon: siteIcon,
      renderLink: <Link to={siteRoutes.index()} />,
    },
    {
      label: t("settings_nav_v3.developers.title"),
      icon: developersIcon,
      renderLink: (
        <Link
          to={
            isOverviewTabEnabled
              ? settingsNavPathV3.developers.overview
              : settingsNavPathV3.developers.api
          }
        />
      ),
    },
    "divider",
    {
      label: t("sidebar_settings"),
      icon: settingsIcon,
      renderLink: <Link to="/settings" />,
    },
    ...(showCustomizeTheme
      ? [
          {
            label: t("customize_theme.title"),
            icon: customizeThemeIcon,
            renderLink: <Link to="/settings/theme" />,
          },
        ]
      : []),
    "divider",
    ...(!isBannerVisible
      ? [
          {
            label: t("view_as"),
            icon: viewAsIcon,
            onClick: () => {
              setTimeout(() => {
                showBanner();
              }, 0);
            },
          },
        ]
      : []),
    {
      label: t("sidebar_invite_member"),
      icon: inviteMemberIcon,
      renderLink: (
        <Link
          to={settingsNavPathV3.members.invite}
          search={validateInviteMembersQueryParams({ from: pathname })}
        />
      ),
    },
  ];

  return options;
};
